import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useUser } from '../../../shared/context/UserContext';
import { useProject } from '../../../shared/context/ProjectContext';
import { useReferenceData } from '../../../shared/hooks/useReferenceData';
import { updateLifecycleProfile, getAssessmentHistory } from '../api/lifecycleApi';
import { updateInventoryTool } from '../../inventory/api/inventoryApi';
import { getMaterialSuggestion } from '../../../shared/utils/toolMaterialUtils';
import ScrDocumentsPanel from './ScrDocumentsPanel';
import {
  getAssetTags, addAssetTag, patchAssetTag, deleteAssetTag,
  getPartNumbers, addPartNumber, patchPartNumber, deletePartNumber,
} from '../api/reconApi';
import PartNumbersList from '../../../shared/components/PartNumbersList';
import AssetTagsList from '../../../shared/components/AssetTagsList';
import { comparePartNumbers } from '../../../shared/utils/reconSort';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import PhotoGalleryPanel from '../../../shared/components/PhotoGalleryPanel';
import './LifecycleDetailPanel.css';

/* ── Human-readable labels for completeness fields ───────────────────── */
const FIELD_LABELS = {
  warranty_shots: 'Warranty Pieces',
  actual_shots_to_date: 'Actual Pieces',
  weekly_capacity: 'Weekly Capacity',
  current_condition_score: 'Condition Score',
  warranty_shots_source: 'Warranty Source',
  cavity_number_start: 'Cavity Start',
  cavity_number_end: 'Cavity End',
  lifecycle_detail: 'Location Detail',
  assessment_notes: 'Assessment Notes',
  scr_status: 'SCR Status',
  ownership: 'Ownership',
  service_type: 'Service Type',
  part_numbers: 'Part Numbers',
  demand_forecast: 'Demand Forecast',
};

function humanLabel(key) {
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/* ── Condition Score info popover ─────────────────────────────────────── */
const CONDITION_DEFINITIONS = [
  {
    level: 1, title: 'As good as new',
    desc: 'Tool displays only minor wear. Expected lifetime not reached. Parts within specification.',
    action: 'Continue normal TPM.',
  },
  {
    level: 2, title: 'Good condition',
    desc: 'Normal signs of usage and wear & tear. Expected lifetime not reached. Parts within specification.',
    action: 'Continue normal TPM.',
  },
  {
    level: 3, title: 'Fair condition',
    desc: 'Usable but showing signs of deterioration. Expected lifetime not reached. Parts within specification but dimension trend shows decreasing capability.',
    action: 'Increase TPM frequency, start preparations to refurbish or renew tooling.',
  },
  {
    level: 4, title: 'Poor condition',
    desc: 'Significant damage present, need for refurbishment or new tool. Expected lifetime exceeded. Frequent adjustments during production necessary to achieve part specification.',
    action: 'Increase TPM, possible CSL1 on defined characteristics. New tool required if serial deliveries continuing.',
  },
];

function ConditionInfoButton() {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (btnRef.current && !btnRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function handleKey(e) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  return (
    <span className="ldp-info-wrap" ref={btnRef}>
      <button
        type="button"
        className="ldp-info-btn"
        onClick={() => setOpen(v => !v)}
        aria-label="Condition score definitions"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <path d="M12 16v-4"/>
          <path d="M12 8h.01"/>
        </svg>
      </button>
      {open && (
        <>
          <div className="ldp-info-backdrop" onClick={() => setOpen(false)} />
          <div className="ldp-info-popover">
            <div className="ldp-info-popover-header">
              <span className="ldp-info-popover-title">Autoliv TLM Condition Ratings</span>
              <button type="button" className="ldp-info-popover-close" onClick={() => setOpen(false)}>&times;</button>
            </div>
            {CONDITION_DEFINITIONS.map(d => (
              <div key={d.level} className="ldp-info-level">
                <div className="ldp-info-level-head">Level {d.level} &mdash; {d.title}</div>
                <div className="ldp-info-level-desc">{d.desc}</div>
                <div className="ldp-info-level-action"><strong>Action:</strong> {d.action}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </span>
  );
}

/* ── Default section order ────────────────────────────────────────────── */
const DEFAULT_ORDER = [
  'shot_counts', 'eow_status', 'condition', 'photos', 'scr_tracking',
  'part_numbers', 'asset_tags', 'autoliv_import', 'financial', 'system_fields',
];

function getPanelOrderKey(email) {
  return `tmg_lifecycle_panel_order_${email || 'default'}`;
}

function loadPanelOrder(email) {
  try {
    const raw = localStorage.getItem(getPanelOrderKey(email));
    if (!raw) return [...DEFAULT_ORDER];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [...DEFAULT_ORDER];
    // Drop any stale keys no longer in DEFAULT_ORDER, then append any new
    // DEFAULT_ORDER keys not yet in the user's saved order. Preserves any
    // custom rearrangement while seamlessly absorbing schema additions.
    const validParsed = parsed.filter(k => DEFAULT_ORDER.includes(k));
    const missingKeys = DEFAULT_ORDER.filter(k => !validParsed.includes(k));
    return [...validParsed, ...missingKeys];
  } catch { /* ignore */ }
  return [...DEFAULT_ORDER];
}

function savePanelOrder(email, order) {
  try {
    localStorage.setItem(getPanelOrderKey(email), JSON.stringify(order));
  } catch { /* ignore */ }
}

/* ── Sortable Section wrapper ─────────────────────────────────────────── */
function SortableSection({ id, title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`ldp-section${isDragging ? ' ldp-section-dragging' : ''}`}
    >
      <div className="ldp-section-header">
        <span className="ldp-drag-handle" {...attributes} {...listeners}>&#10495;</span>
        <button
          className="ldp-section-toggle"
          onClick={() => setOpen(!open)}
          type="button"
          style={{ display: 'contents' }}
        >
          <span className="ldp-section-title">{title}</span>
        </button>
        <span className="ldp-section-header-right">
          <button
            className="ldp-section-chevron-btn"
            onClick={() => setOpen(!open)}
            type="button"
          >
            <span className={`ldp-section-chevron ${open ? 'open' : ''}`}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
          </button>
        </span>
      </div>
      {open && <div className="ldp-section-body">{children}</div>}
    </div>
  );
}

/* ── TapButtons — same pattern as inventory screen ───────────────────── */
function TapButtons({ options, value, onChange }) {
  return (
    <div className="td-tap-group td-tap-large">
      {options.map(opt => (
        <button
          key={opt.value}
          className={`td-tap-btn ${value === opt.value ? 'active' : ''} ${opt.cls || ''}`}
          onClick={() => onChange(opt.value)}
          type="button"
        >
          <span className="td-tap-label">{opt.label}</span>
          {opt.sublabel && <span className="td-tap-sublabel">{opt.sublabel}</span>}
        </button>
      ))}
    </div>
  );
}

/* ── Disposition badge ───────────────────────────────────────────────── */
const DISP_CONFIG = {
  URGENT:     { label: 'Urgent',     cls: 'ldp-disp-urgent' },
  NEAR_TERM:  { label: 'Near-term',  cls: 'ldp-disp-near' },
  MONITORING: { label: 'Monitoring', cls: 'ldp-disp-monitor' },
};

function DispBadge({ value }) {
  const cfg = DISP_CONFIG[value];
  if (!cfg) return null;
  return <span className={`ldp-badge ${cfg.cls}`}>{cfg.label}</span>;
}

/* ── Format helpers ──────────────────────────────────────────────────── */
function fmtDate(v) {
  if (!v) return null;
  // A stored date-only value (YYYY-MM-DD) is formatted from its parts: new Date() would read
  // it as UTC midnight and show the day before west of UTC (B-292). Timestamps keep the Date path.
  const ymd = typeof v === 'string' && /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (ymd) return `${Number(ymd[2])}/${Number(ymd[3])}/${ymd[1]}`;
  try { return new Date(v).toLocaleDateString(); } catch { return v; }
}
function fmtDatetime(v) {
  if (!v) return null;
  try { return new Date(v).toLocaleString(); } catch { return v; }
}
function fmtNumber(v) {
  if (v == null) return null;
  return Number(v).toLocaleString();
}

// Tools container fields rendered in this panel.
// Fields in this list route to update_tool on save. All others route to
// manage_lifecycle_profile.
// Add new Tools fields here as they are added to the panel UI.
// cavities is the static Tools-doc cavity COUNT (distinct from the LP-doc
// cavity_number_start/_end range, which is NOT a TOOLS_FIELDS member).
// barcode + qr_code are Tools-level identifiers — they MUST be here so the
// dual-save routes them to update_tool, NOT manage_lifecycle_profile.
const TOOLS_FIELDS = new Set(['material', 'cavities', 'barcode', 'qr_code']);

/* ═══════════════════════════════════════════════════════════════════════ */
export default function LifecycleDetailPanel({ tool, onSaved }) {
  const { callerEmail, userProfile } = useUser();
  const isSupplier = userProfile?.role === 'SUPPLIER_USER';
  const { currentProject } = useProject();
  const toolId = tool?.id || tool?.tool_id;
  const siteId = tool?.site_id;

  /* ── ReferenceData ─────────────────────────────────────────────────── */
  // eslint-disable-next-line no-unused-vars
  const { options: refOpts, addValue: addRefValue } = useReferenceData(
    ['warranty_shots_source', 'scr_status', 'scr_type', 'ownership',
     'service_type', 'lifecycle_detail', 'part_number_type',
     'material', 'tool_type_material_map',
     'cost_type', 'original_asset_currency'],
    callerEmail,
    siteId
  );

  /* ── Form state — always editable ──────────────────────────────────── */
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [reconCloseError, setReconCloseError] = useState(null);

  // Re-init form when tool changes
  useEffect(() => {
    if (!tool) return;
    setForm({
      list_number:                  tool.list_number ?? '',
      material:                     tool.material ?? '',
      cavities:                     tool.cavities ?? '',
      barcode:                      tool.barcode ?? '',
      qr_code:                      tool.qr_code ?? '',
      actual_shots_to_date:         tool.actual_shots_to_date ?? '',
      warranty_shots:               tool.warranty_shots ?? '',
      weekly_capacity:              tool.weekly_capacity ?? '',
      warranty_shots_source:        tool.warranty_shots_source ?? '',
      current_condition_score:      tool.current_condition_score ?? null,
      assessment_notes:             tool.assessment_notes ?? '',
      scr_status:                   tool.scr_status ?? '',
      scr_type:                     tool.scr_type ?? '',
      scr_submitted_date:           tool.scr_submitted_date ?? '',
      agreed_solution:              tool.agreed_solution ?? '',
      scr_notes:                    tool.scr_notes ?? '',
      scr_identifier:               tool.scr_identifier ?? '',
      scr_documents:                tool.scr_documents || [],
      ownership:                    tool.ownership ?? '',
      service_type:                 tool.service_type ?? '',
      shift_pattern:                tool.shift_pattern ?? '',
      flag_yellow:                  !!tool.flag_yellow,
      flag_orange:                  !!tool.flag_orange,
      lifecycle_detail:             tool.lifecycle_detail ?? '',
      cavity_number_start:          tool.cavity_number_start ?? '',
      cavity_number_end:            tool.cavity_number_end ?? '',
      disposal_request_submitted_date: tool.disposal_request_submitted_date ?? '',
      original_asset_value:         tool.original_asset_value ?? '',
      original_asset_currency:      tool.original_asset_currency ?? '',
      cost_type:                    tool.cost_type ?? '',
      finished_goods_on_hand:       tool.finished_goods_on_hand ?? '',
      work_in_process:              tool.work_in_process ?? '',
      raw_materials_on_hand:        tool.raw_materials_on_hand ?? '',
      recon_status:                 tool.recon_status ?? 'OPEN',
    });
    setSaveMsg(null);
    setReconCloseError(null);
  }, [tool]);

  /* ── Recon status toggle with close-gate ─────────────────────────── */
  function handleReconToggle(newStatus) {
    setReconCloseError(null);
    if (newStatus === 'CLOSED') {
      const comp = tool?.completeness;
      if (!comp?.is_complete) {
        const missing = (comp?.missing_fields || []).map(humanLabel);
        setReconCloseError(
          `Cannot close — missing: ${missing.length ? missing.join(', ') : 'unknown fields'}`
        );
        return;
      }
    }
    set('recon_status', newStatus);
  }

  function set(key, val) {
    setForm(prev => ({ ...prev, [key]: val }));
  }

  // tool_type is not edited in this panel — watch the incoming tool prop.
  useEffect(() => {
    if (!refOpts.tool_type_material_map) return;
    const suggestion = getMaterialSuggestion(
      tool?.tool_type,
      form.material,
      refOpts.tool_type_material_map
    );
    if (suggestion) set('material', suggestion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool?.tool_type, refOpts.tool_type_material_map]);

  /* ── Part Numbers state ────────────────────────────────────────────── */
  const [partNumbers, setPartNumbers] = useState([]);
  const [newPN, setNewPN] = useState('');
  const [newPNType, setNewPNType] = useState('TBD');
  const [newPNDesc, setNewPNDesc] = useState('');
  const [pnError, setPnError] = useState(null);
  const addingPNRef = useRef(false);

  /* ── Asset Tags state ──────────────────────────────────────────────── */
  const [assetTags, setAssetTags] = useState([]);
  const [newTagVal, setNewTagVal] = useState('');
  const [atError, setAtError] = useState(null);
  const addingTagRef = useRef(false);

  // Display-only sort: a sorted COPY for render. State + handlers stay on the
  // original `partNumbers` (by-id), so stored order is never reordered.
  const sortedPartNumbers = useMemo(
    () => [...partNumbers].sort(comparePartNumbers), [partNumbers]);

  /* ── Assessment History state ──────────────────────────────────────── */
  const [assessments, setAssessments] = useState([]);
  const [assessmentsLoaded, setAssessmentsLoaded] = useState(false);

  // Load part numbers + asset tags when tool changes
  useEffect(() => {
    if (!toolId || !callerEmail) return;
    getPartNumbers(toolId, callerEmail).then(setPartNumbers).catch(() => setPartNumbers([]));
    getAssetTags(toolId, callerEmail).then(setAssetTags).catch(() => setAssetTags([]));
    setAssessmentsLoaded(false);
    setAssessments([]);
  }, [toolId, callerEmail]);

  /* ── Part Number handlers ──────────────────────────────────────────── */
  async function handleAddPN() {
    if (addingPNRef.current || !newPN.trim() || !newPNType) return;
    addingPNRef.current = true;
    try {
      const created = await addPartNumber({
        toolId, siteId, partNumber: newPN.trim(),
        partNumberType: newPNType, partDescription: newPNDesc.trim(), callerEmail,
      });
      setPartNumbers(prev => [...prev, created]);
      setNewPN(''); setNewPNDesc('');
    } catch (err) { console.error('Add PN failed:', err); }
    finally { addingPNRef.current = false; }
  }

  async function handleDeletePN(id) {
    try {
      await deletePartNumber({ partNumberId: id, toolId, callerEmail });
      setPartNumbers(prev => prev.filter(p => p.id !== id));
    } catch (err) { console.error('Delete PN failed:', err); }
  }

  async function handleEditPN(partNumberId, { part_number, part_number_type, part_description }) {
    setPnError(null);
    try {
      const updated = await patchPartNumber({
        partNumberId,
        partNumber: part_number,
        partNumberType: part_number_type,
        partDescription: part_description,
        callerEmail,
      });
      setPartNumbers(prev => prev.map(p => p.id === partNumberId ? updated : p));
    } catch (err) {
      setPnError(err.message);
      throw err;
    }
  }

  /* ── Asset Tag handlers ────────────────────────────────────────────── */
  async function handleAddTag() {
    if (addingTagRef.current || !newTagVal.trim()) return;
    addingTagRef.current = true;
    try {
      const created = await addAssetTag({ toolId, siteId, tagValue: newTagVal.trim(), callerEmail });
      setAssetTags(prev => [...prev, created]);
      setNewTagVal('');
    } catch (err) { console.error('Add tag failed:', err); }
    finally { addingTagRef.current = false; }
  }

  async function handleDeleteTag(tagId) {
    try {
      await deleteAssetTag({ tagId, toolId, callerEmail });
      setAssetTags(prev => prev.filter(t => t.id !== tagId));
    } catch (err) { console.error('Delete tag failed:', err); }
  }

  async function handleEditTag(tagId, { tag_value }) {
    setAtError(null);
    try {
      const updated = await patchAssetTag({ tagId, tagValue: tag_value, callerEmail });
      setAssetTags(prev => prev.map(t => t.id === tagId ? updated : t));
    } catch (err) {
      setAtError(err.message);
      throw err;
    }
  }

  /* ── Assessment History loader ─────────────────────────────────────── */
  const loadAssessments = useCallback(() => {
    if (assessmentsLoaded || !toolId) return;
    setAssessmentsLoaded(true);
    getAssessmentHistory(toolId, callerEmail)
      .then(list => setAssessments((list || []).slice(0, 10)))
      .catch(() => setAssessments([]));
  }, [toolId, callerEmail, assessmentsLoaded]);

  /* ── Save ──────────────────────────────────────────────────────────── */
  const handleSave = useCallback(async () => {
    if (!toolId) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const payload = {};
      const intFields = [
        'actual_shots_to_date', 'warranty_shots', 'weekly_capacity',
        'cavity_number_start', 'cavity_number_end', 'cavities',
        'finished_goods_on_hand', 'work_in_process', 'raw_materials_on_hand',
        'original_asset_value',
      ];
      const strFields = [
        'list_number', 'warranty_shots_source', 'assessment_notes',
        'scr_status', 'scr_type', 'scr_submitted_date', 'agreed_solution', 'scr_notes', 'scr_identifier',
        'ownership', 'service_type', 'shift_pattern',
        'lifecycle_detail', 'disposal_request_submitted_date',
        'disposition_category', 'recon_status',
        'material', 'barcode', 'qr_code',
        'original_asset_currency', 'cost_type',
      ];
      const boolFields = ['flag_yellow', 'flag_orange'];

      for (const k of intFields) {
        const v = form[k];
        if (v !== '' && v != null) payload[k] = Number(v);
      }
      if (form.current_condition_score != null) {
        payload.current_condition_score = Number(form.current_condition_score);
      }
      for (const k of strFields) {
        if (form[k] !== undefined) payload[k] = form[k];
      }
      for (const k of boolFields) {
        payload[k] = !!form[k];
      }
      // Array fields — full replace
      if (form.scr_documents !== undefined) {
        payload.scr_documents = form.scr_documents;
      }

      const toolPayload = {};
      const lifecyclePayload = {};
      for (const [k, v] of Object.entries(payload)) {
        if (TOOLS_FIELDS.has(k)) toolPayload[k] = v;
        else lifecyclePayload[k] = v;
      }

      const [, updated] = await Promise.all([
        Object.keys(toolPayload).length
          ? updateInventoryTool(toolId, toolPayload, callerEmail)
          : Promise.resolve(null),
        Object.keys(lifecyclePayload).length
          ? updateLifecycleProfile(toolId, lifecyclePayload, callerEmail, currentProject?.id)
          : Promise.resolve(null),
      ]);
      // Merge returned document into form so displayed values reflect saved state
      if (updated && typeof updated === 'object') {
        setForm(prev => {
          const next = { ...prev };
          for (const k of Object.keys(next)) {
            if (updated[k] !== undefined) {
              if (k === 'flag_yellow' || k === 'flag_orange') {
                next[k] = !!updated[k];
              } else {
                next[k] = updated[k] ?? (typeof prev[k] === 'string' ? '' : prev[k]);
              }
            }
          }
          return next;
        });
      }
      setSaveMsg('success');
      setTimeout(() => setSaveMsg(null), 2000);
      if (onSaved) onSaved();
    } catch (err) {
      setSaveMsg(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [toolId, form, callerEmail, onSaved, currentProject]);

  /* ── Panel order (drag-to-reorder) ──────────────────────────────────── */
  const [panelOrder, setPanelOrder] = useState(() => loadPanelOrder(callerEmail));

  // Sync when callerEmail changes
  useEffect(() => {
    setPanelOrder(loadPanelOrder(callerEmail));
  }, [callerEmail]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event) {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setPanelOrder(prev => {
        const oldIdx = prev.indexOf(active.id);
        const newIdx = prev.indexOf(over.id);
        const next = arrayMove(prev, oldIdx, newIdx);
        savePanelOrder(callerEmail, next);
        return next;
      });
    }
  }

  function handleResetOrder() {
    localStorage.removeItem(getPanelOrderKey(callerEmail));
    setPanelOrder(DEFAULT_ORDER);
  }

  // Build ordered section list — assessment_history appended if not in stored order
  const allSectionIds = useMemo(() => {
    if (!panelOrder.includes('assessment_history')) {
      return [...panelOrder, 'assessment_history'];
    }
    return panelOrder;
  }, [panelOrder]);

  if (!tool) return null;

  /* ── Derived values ────────────────────────────────────────────────── */
  const shotsRemaining =
    form.warranty_shots !== '' && form.warranty_shots != null &&
    form.actual_shots_to_date !== '' && form.actual_shots_to_date != null
      ? Number(form.warranty_shots) - Number(form.actual_shots_to_date)
      : null;

  const pnDisplay = partNumbers.length > 0
    ? partNumbers.map(p => p.part_number).join(', ')
    : 'No part numbers';

  const showScrFields = form.scr_status && form.scr_status !== 'NOT_REQUIRED';

  /* ── Section content map ────────────────────────────────────────────── */
  const sectionMap = {
    shot_counts: (
      <SortableSection key="shot_counts" id="shot_counts" title="Piece Counts" defaultOpen={true}>
        <div className="ldp-grid-2">
          <label className="ldp-field">
            <span className="ldp-label">Actual Pieces to Date</span>
            <input type="number" className="lrc-input"
              value={form.actual_shots_to_date ?? ''}
              onChange={e => set('actual_shots_to_date', e.target.value)} />
          </label>
          <label className="ldp-field">
            <span className="ldp-label">Warranty Pieces</span>
            <input type="number" className="lrc-input"
              value={form.warranty_shots ?? ''}
              onChange={e => set('warranty_shots', e.target.value)} />
          </label>
          <label className="ldp-field">
            <span className="ldp-label">Weekly Capacity</span>
            <input type="number" className="lrc-input"
              value={form.weekly_capacity ?? ''}
              onChange={e => set('weekly_capacity', e.target.value)} />
          </label>
          <div className="ldp-field">
            <span className="ldp-label">Pieces Remaining</span>
            <span className={`lrc-field-value${shotsRemaining == null ? ' empty' : ''}`}>
              {shotsRemaining != null ? fmtNumber(shotsRemaining) : '—'}
            </span>
          </div>
          <label className="ldp-field">
            <span className="ldp-label">Warranty Source</span>
            <select className="lrc-input" value={form.warranty_shots_source}
              onChange={e => set('warranty_shots_source', e.target.value)}>
              <option value="">— Select —</option>
              {(refOpts.warranty_shots_source || []).map(v => <option key={v} value={v}>{v}</option>)}
              {form.warranty_shots_source && !(refOpts.warranty_shots_source || []).includes(form.warranty_shots_source) && (
                <option value={form.warranty_shots_source}>{form.warranty_shots_source}</option>
              )}
            </select>
          </label>
          <div className="ldp-field">
            <span className="ldp-label">Pieces Last Updated</span>
            <span className={`lrc-field-value${!tool.shots_last_updated_date ? ' empty' : ''}`}>
              {fmtDate(tool.shots_last_updated_date) || '—'}
            </span>
          </div>
          <div className="ldp-field">
            <span className="ldp-label">Last Updated By</span>
            <span className={`lrc-field-value${!tool.shots_last_updated_by ? ' empty' : ''}`}>
              {tool.shots_last_updated_by || '—'}
            </span>
          </div>
        </div>
      </SortableSection>
    ),
    eow_status: (
      <SortableSection key="eow_status" id="eow_status" title="EOW Status" defaultOpen={true}>
        {tool.eow_estimated_date || tool.eow_decision_trigger_date || tool.disposition_category || tool.eow_last_calculated_date ? (
          <div className="ldp-grid-2">
            <div className="ldp-field">
              <span className="ldp-label">EOW Estimated Date</span>
              <span className={`lrc-field-value${!tool.eow_estimated_date ? ' empty' : ''}`}>
                {fmtDate(tool.eow_estimated_date) || 'Not yet calculated'}
              </span>
            </div>
            <div className="ldp-field">
              <span className="ldp-label">EOW Decision Trigger Date</span>
              <span className={`lrc-field-value${!tool.eow_decision_trigger_date ? ' empty' : ''}`}>
                {fmtDate(tool.eow_decision_trigger_date) || 'Not yet calculated'}
              </span>
            </div>
            <div className="ldp-field">
              <span className="ldp-label">Disposition Category</span>
              {tool.disposition_category
                ? <DispBadge value={tool.disposition_category} />
                : <span className="lrc-field-value empty">Not yet calculated</span>
              }
            </div>
            <div className="ldp-field">
              <span className="ldp-label">EOW Last Calculated</span>
              <span className={`lrc-field-value${!tool.eow_last_calculated_date ? ' empty' : ''}`}>
                {fmtDatetime(tool.eow_last_calculated_date) || 'Not yet calculated'}
              </span>
            </div>
            <div className="ldp-field">
              <span className="ldp-label">Weekly Demand Avg</span>
              <span className={`lrc-field-value${tool.demand_weekly_average == null ? ' empty' : ''}`}>
                {tool.demand_weekly_average != null ? tool.demand_weekly_average : '\u2014'}
              </span>
            </div>
          </div>
        ) : (
          <p className="ldp-records-empty">
            {tool.warranty_shots && tool.weekly_capacity && tool.actual_shots_to_date != null && tool.current_condition_score == null
              ? 'Piece data complete. Enter a Condition Score above to calculate EOW.'
              : 'EOW date will calculate automatically once Warranty Pieces, Weekly Capacity, and Condition Score are entered.'
            }
          </p>
        )}
      </SortableSection>
    ),
    condition: (
      <SortableSection key="condition" id="condition" title="Condition" defaultOpen={true}>
        <span className="ldp-label">Current Condition Score <ConditionInfoButton /></span>
        <TapButtons
          value={form.current_condition_score}
          onChange={v => set('current_condition_score', v)}
          options={[
            { value: 1, label: '1', sublabel: 'As good as new', cls: 'green' },
            { value: 2, label: '2', sublabel: 'Good condition', cls: 'amber' },
            { value: 3, label: '3', sublabel: 'Fair condition', cls: 'orange' },
            { value: 4, label: '4', sublabel: 'Poor condition', cls: 'red' },
          ]}
        />
        <div className="ldp-grid-2 ldp-mt">
          <div className="ldp-field">
            <span className="ldp-label">Last Assessed</span>
            <span className={`lrc-field-value${!tool.condition_last_assessed_date ? ' empty' : ''}`}>
              {fmtDate(tool.condition_last_assessed_date) || '—'}
            </span>
          </div>
          <div className="ldp-field">
            <span className="ldp-label">Assessed By</span>
            <span className={`lrc-field-value${!tool.condition_last_assessed_by ? ' empty' : ''}`}>
              {tool.condition_last_assessed_by || '—'}
            </span>
          </div>
        </div>
        <label className="ldp-field ldp-mt">
          <span className="ldp-label">Assessment Notes</span>
          <textarea className="lrc-input ldp-textarea"
            value={form.assessment_notes}
            onChange={e => set('assessment_notes', e.target.value)}
            rows={3}
          />
        </label>
      </SortableSection>
    ),
    photos: (
      <SortableSection key="photos" id="photos" title="Photos" defaultOpen={false}>
        <PhotoGalleryPanel toolId={toolId} callerEmail={callerEmail} />
      </SortableSection>
    ),
    scr_tracking: (
      <SortableSection key="scr_tracking" id="scr_tracking" title="SCR Tracking" defaultOpen={true}>
        <div className="ldp-grid-2">
          <label className="ldp-field">
            <span className="ldp-label">SCR Status</span>
            <select className="lrc-input" value={form.scr_status}
              onChange={e => set('scr_status', e.target.value)}>
              <option value="">— Select —</option>
              {(refOpts.scr_status || ['NOT_REQUIRED', 'DEFERRED', 'PENDING_SUBMISSION', 'SUBMITTED', 'APPROVED', 'REJECTED', 'COMPLETE']).map(v =>
                <option key={v} value={v}>{v}</option>
              )}
              {form.scr_status && !(refOpts.scr_status || ['NOT_REQUIRED', 'DEFERRED', 'PENDING_SUBMISSION', 'SUBMITTED', 'APPROVED', 'REJECTED', 'COMPLETE']).includes(form.scr_status) && (
                <option value={form.scr_status}>{form.scr_status}</option>
              )}
            </select>
          </label>
          {showScrFields && (
            <label className="ldp-field">
              <span className="ldp-label">SCR Type</span>
              <select className="lrc-input" value={form.scr_type}
                onChange={e => set('scr_type', e.target.value)}>
                <option value="">— Select —</option>
                {(refOpts.scr_type || ['REPLACEMENT', 'REFURBISHMENT']).map(v =>
                  <option key={v} value={v}>{v}</option>
                )}
                {form.scr_type && !(refOpts.scr_type || ['REPLACEMENT', 'REFURBISHMENT']).includes(form.scr_type) && (
                  <option value={form.scr_type}>{form.scr_type}</option>
                )}
              </select>
            </label>
          )}
          {showScrFields && (
            <label className="ldp-field">
              <span className="ldp-label">SCR Submitted Date</span>
              <input type="date" className="lrc-input"
                value={form.scr_submitted_date}
                disabled={isSupplier}
                onChange={e => set('scr_submitted_date', e.target.value)} />
            </label>
          )}
          <label className="ldp-field">
            <span className="ldp-label">Agreed Solution</span>
            <input type="text" className="lrc-input"
              value={form.agreed_solution}
              disabled={isSupplier}
              onChange={e => set('agreed_solution', e.target.value)}
              placeholder="Agreed solution" />
            <span className="ldp-field-hint">Imported from Autoliv — update if changed</span>
          </label>
          <label className="ldp-field">
            <span className="ldp-label">SCR Identifier</span>
            <input type="text" className="lrc-input"
              value={form.scr_identifier}
              onChange={e => set('scr_identifier', e.target.value)}
              placeholder="Autoliv SCR number or code" />
          </label>
        </div>
        <label className="ldp-field" style={{ marginTop: 'var(--space-2)' }}>
          <span className="ldp-label">SCR Notes</span>
          <textarea className="lrc-input"
            value={form.scr_notes}
            onChange={e => set('scr_notes', e.target.value)}
            rows={4}
            placeholder="Describe the supplier's decision rationale, repair history, or any relevant context..." />
        </label>
        <div style={{ marginTop: 'var(--space-3)' }}>
          <ScrDocumentsPanel
            toolId={toolId}
            lifecycleId={tool?.id}
            documents={form.scr_documents || []}
            onChange={docs => set('scr_documents', docs)}
            callerEmail={callerEmail}
            projectId={currentProject?.id}
            readOnly={false}
          />
        </div>
      </SortableSection>
    ),
    part_numbers: (
      <SortableSection key="part_numbers" id="part_numbers" title="Part Numbers" defaultOpen={true}>
        <PartNumbersList
          partNumbers={sortedPartNumbers}
          error={pnError}
          onAdd={handleAddPN}
          onEdit={handleEditPN}
          onDelete={handleDeletePN}
          newPn={newPN}
          setNewPn={setNewPN}
          newPnType={newPNType}
          setNewPnType={setNewPNType}
          newPnDesc={newPNDesc}
          setNewPnDesc={setNewPNDesc}
          partNumberTypeOptions={refOpts.part_number_type}
        />
      </SortableSection>
    ),
    asset_tags: (
      <SortableSection key="asset_tags" id="asset_tags" title="Asset Tags" defaultOpen={false}>
        <AssetTagsList
          assetTags={assetTags}
          error={atError}
          onAdd={handleAddTag}
          onEdit={handleEditTag}
          onDelete={handleDeleteTag}
          newTag={newTagVal}
          setNewTag={setNewTagVal}
        />
      </SortableSection>
    ),
    autoliv_import: (
      <SortableSection key="autoliv_import" id="autoliv_import" title="Autoliv Import Fields" defaultOpen={false}>
        <div className="ldp-grid-2">
          <label className="ldp-field">
            <span className="ldp-label">Ownership</span>
            <select className="lrc-input" value={form.ownership}
              onChange={e => set('ownership', e.target.value)}>
              <option value="">— Select —</option>
              {(refOpts.ownership || []).map(v => <option key={v} value={v}>{v}</option>)}
              {form.ownership && !(refOpts.ownership || []).includes(form.ownership) && (
                <option value={form.ownership}>{form.ownership}</option>
              )}
            </select>
          </label>
          <label className="ldp-field">
            <span className="ldp-label">Service Type</span>
            <select className="lrc-input" value={form.service_type}
              onChange={e => set('service_type', e.target.value)}>
              <option value="">— Select —</option>
              {(refOpts.service_type || []).map(v => <option key={v} value={v}>{v}</option>)}
              {form.service_type && !(refOpts.service_type || []).includes(form.service_type) && (
                <option value={form.service_type}>{form.service_type}</option>
              )}
            </select>
          </label>
          <label className="ldp-field">
            <span className="ldp-label">Shift Pattern</span>
            <input type="text" className="lrc-input" value={form.shift_pattern}
              onChange={e => set('shift_pattern', e.target.value)} />
          </label>
          <div className="ldp-field">
            <label className="ldp-checkbox">
              <input type="checkbox" checked={form.flag_yellow}
                onChange={e => set('flag_yellow', e.target.checked)} />
              <span>Flag Yellow (Autoliv moderate risk)</span>
            </label>
          </div>
          <div className="ldp-field">
            <label className="ldp-checkbox">
              <input type="checkbox" checked={form.flag_orange}
                onChange={e => set('flag_orange', e.target.checked)} />
              <span>Flag Orange (Autoliv elevated risk)</span>
            </label>
          </div>
          <label className="ldp-field">
            <span className="ldp-label">Lifecycle Detail</span>
            <select className="lrc-input" value={form.lifecycle_detail}
              onChange={e => set('lifecycle_detail', e.target.value)}>
              <option value="">— Select —</option>
              {(refOpts.lifecycle_detail || []).map(v => <option key={v} value={v}>{v}</option>)}
              {form.lifecycle_detail && !(refOpts.lifecycle_detail || []).includes(form.lifecycle_detail) && (
                <option value={form.lifecycle_detail}>{form.lifecycle_detail}</option>
              )}
            </select>
          </label>
          <label className="ldp-field">
            <span className="ldp-label">Material</span>
            <select className="lrc-input" value={form.material}
              onChange={e => set('material', e.target.value)}>
              <option value="">— Select —</option>
              {(refOpts.material || []).map(v => <option key={v} value={v}>{v}</option>)}
              {form.material && !(refOpts.material || []).includes(form.material) && (
                <option value={form.material}>{form.material}</option>
              )}
            </select>
          </label>
          <label className="ldp-field">
            <span className="ldp-label">Cavities</span>
            <input type="number" className="lrc-input" value={form.cavities ?? ''}
              onChange={e => set('cavities', e.target.value)} />
            <span className="ldp-field-hint">Total cavity count (not the numbered range below)</span>
          </label>
          <label className="ldp-field">
            <span className="ldp-label">Cavity Number Start</span>
            <input type="number" className="lrc-input" value={form.cavity_number_start}
              onChange={e => set('cavity_number_start', e.target.value)} />
          </label>
          <label className="ldp-field">
            <span className="ldp-label">Cavity Number End</span>
            <input type="number" className="lrc-input" value={form.cavity_number_end}
              onChange={e => set('cavity_number_end', e.target.value)} />
          </label>
          <label className="ldp-field">
            <span className="ldp-label">Disposal Request Submitted Date</span>
            <input type="date" className="lrc-input" value={form.disposal_request_submitted_date}
              onChange={e => set('disposal_request_submitted_date', e.target.value)} />
          </label>
        </div>
      </SortableSection>
    ),
    financial: (
      <SortableSection key="financial" id="financial" title="Financial / Asset Info" defaultOpen={false}>
        <div className="ldp-grid-2">
          <label className="ldp-field">
            <span className="ldp-label">Original Asset Value</span>
            <input type="number" className="lrc-input"
              value={form.original_asset_value ?? ''}
              onChange={e => set('original_asset_value', e.target.value)} />
          </label>
          <label className="ldp-field">
            <span className="ldp-label">Original Asset Currency</span>
            <select className="lrc-input" value={form.original_asset_currency}
              onChange={e => set('original_asset_currency', e.target.value)}>
              <option value="">— Select —</option>
              {(refOpts.original_asset_currency || []).map(v => <option key={v} value={v}>{v}</option>)}
              {form.original_asset_currency && !(refOpts.original_asset_currency || []).includes(form.original_asset_currency) && (
                <option value={form.original_asset_currency}>{form.original_asset_currency}</option>
              )}
            </select>
          </label>
          <label className="ldp-field">
            <span className="ldp-label">Cost Type</span>
            <select className="lrc-input" value={form.cost_type}
              onChange={e => set('cost_type', e.target.value)}>
              <option value="">— Select —</option>
              {(refOpts.cost_type || []).map(v => <option key={v} value={v}>{v}</option>)}
              {form.cost_type && !(refOpts.cost_type || []).includes(form.cost_type) && (
                <option value={form.cost_type}>{form.cost_type}</option>
              )}
            </select>
          </label>
          <label className="ldp-field">
            <span className="ldp-label">Finished Goods on Hand</span>
            <input type="number" className="lrc-input"
              value={form.finished_goods_on_hand ?? ''}
              onChange={e => set('finished_goods_on_hand', e.target.value)} />
          </label>
          <label className="ldp-field">
            <span className="ldp-label">Work in Process</span>
            <input type="number" className="lrc-input"
              value={form.work_in_process ?? ''}
              onChange={e => set('work_in_process', e.target.value)} />
          </label>
          <label className="ldp-field">
            <span className="ldp-label">Raw Materials on Hand</span>
            <input type="number" className="lrc-input"
              value={form.raw_materials_on_hand ?? ''}
              onChange={e => set('raw_materials_on_hand', e.target.value)} />
          </label>
        </div>
      </SortableSection>
    ),
    system_fields: (
      <SortableSection key="system_fields" id="system_fields" title="System Fields" defaultOpen={false}>
        <div className="ldp-grid-2">
          <div className="ldp-field">
            <span className="ldp-label">Created At</span>
            <span className="lrc-field-value">{fmtDatetime(tool.created_at) || '—'}</span>
          </div>
          <div className="ldp-field">
            <span className="ldp-label">Created By</span>
            <span className="lrc-field-value">{tool.created_by || '—'}</span>
          </div>
          <div className="ldp-field">
            <span className="ldp-label">Updated At</span>
            <span className="lrc-field-value">{fmtDatetime(tool.updated_at) || '—'}</span>
          </div>
          <div className="ldp-field">
            <span className="ldp-label">Updated By</span>
            <span className="lrc-field-value">{tool.updated_by || '—'}</span>
          </div>
        </div>
      </SortableSection>
    ),
  };

  /* ── Assessment History section (not sortable — lazy load special case) */
  const assessmentSection = (
    <SortableSection key="assessment_history" id="assessment_history" title="Assessment History" defaultOpen={false}>
      <AssessmentHistoryBody
        assessments={assessments}
        loaded={assessmentsLoaded}
        onExpand={loadAssessments}
      />
    </SortableSection>
  );

  const allSectionMap = { ...sectionMap, assessment_history: assessmentSection };

  return (
    <div className="ldp-panel">
      {/* ── Sticky top: header + save ─────────────────────────────────── */}
      <div className="ldp-sticky-top">
        <div className="ldp-header-strip">
          <div className="ldp-header-top">
            <h2 className="ldp-tool-id">{tool.supplier_tool_id || tool.tool_id || toolId}</h2>
            {tool.alt_tool_id && (
              <span className="ldp-alt-id">{tool.alt_tool_id}</span>
            )}
          </div>
          <div className="ldp-header-meta">
            <span className="ldp-label" style={{ margin: 0, marginRight: 'var(--space-2)' }}>Site:</span>
            <span className="ldp-pn-list">{tool.site_name || tool.supplier_name || '—'}</span>
          </div>
          <div className="ldp-header-meta">
            <span className="ldp-label" style={{ margin: 0, marginRight: 'var(--space-2)' }}>OEM:</span>
            <span className="ldp-pn-list" style={{ marginRight: 'var(--space-6)' }}>{tool.oem || '—'}</span>
            <span className="ldp-label" style={{ margin: 0, marginRight: 'var(--space-2)' }}>Status:</span>
            {tool.workflow_status
              ? <span className="ldp-badge ldp-badge-workflow">{tool.workflow_status}</span>
              : <span className="ldp-pn-list">—</span>
            }
            {tool.disposition_category && (
              <>
                <span className="ldp-label" style={{ margin: 0, marginLeft: 'var(--space-8)', marginRight: 'var(--space-2)' }}>EOW:</span>
                <DispBadge value={tool.disposition_category} />
              </>
            )}
          </div>
          <div className="ldp-header-meta">
            <span className="ldp-label" style={{ margin: 0, marginRight: 'var(--space-2)' }}>Parts:</span>
            <span className="ldp-pn-list">{pnDisplay === 'No part numbers' ? 'None' : pnDisplay}</span>
          </div>
          <div className="ldp-header-fields-row">
            <div className="ldp-header-field">
              <label className="ldp-label">List</label>
              <input
                type="text"
                className="lrc-input ldp-list-input"
                value={form.list_number}
                onChange={e => set('list_number', e.target.value)}
              />
            </div>
            <div className="ldp-header-field">
              <label className="ldp-label">Barcode</label>
              <input
                type="text"
                className="lrc-input ldp-list-input"
                value={form.barcode}
                onChange={e => set('barcode', e.target.value)}
              />
            </div>
            <div className="ldp-header-field">
              <label className="ldp-label">QR Code</label>
              <input
                type="text"
                className="lrc-input ldp-list-input"
                value={form.qr_code}
                onChange={e => set('qr_code', e.target.value)}
              />
            </div>
            <div className="ldp-header-field">
              <label className="ldp-label">Recon Status</label>
              <div className="ldp-recon-toggle">
                <button
                  type="button"
                  className={`ldp-recon-btn ${form.recon_status === 'OPEN' ? 'active open' : ''}`}
                  onClick={() => handleReconToggle('OPEN')}
                >Open</button>
                <button
                  type="button"
                  className={`ldp-recon-btn ${form.recon_status === 'CLOSED' ? 'active closed' : ''}`}
                  onClick={() => handleReconToggle('CLOSED')}
                >Closed</button>
              </div>
              {reconCloseError && (
                <div className="ldp-recon-error">{reconCloseError}</div>
              )}
            </div>
          </div>
        </div>
        <div className="ldp-save-bar">
          <button
            className="lrc-btn lrc-btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
          {saveMsg === 'success' && (
            <span className="ldp-save-ok">Saved</span>
          )}
          {saveMsg && saveMsg !== 'success' && (
            <span className="ldp-save-err">{saveMsg}</span>
          )}
        </div>
      </div>

      {/* ── Sortable sections ─────────────────────────────────────────── */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={allSectionIds} strategy={verticalListSortingStrategy}>
          {allSectionIds.map(id => allSectionMap[id] || null)}
        </SortableContext>
      </DndContext>

      <button className="ldp-reset-order" onClick={handleResetOrder} type="button">
        Reset panel order
      </button>
    </div>
  );
}

/* ── Assessment History Body — lazy-loads on first expand ─────────── */
function AssessmentHistoryBody({ assessments, loaded, onExpand }) {
  useEffect(() => { if (!loaded) onExpand(); }, [loaded, onExpand]);

  if (!loaded) return <p className="ldp-records-empty">Loading...</p>;
  if (assessments.length === 0) return <p className="ldp-records-empty">No assessments recorded yet. Piece count and condition score changes will appear here automatically.</p>;

  return (
    <div className="ldp-records-list">
      {assessments.map((a, i) => {
        let summary = a.assessment_type;
        if (a.assessment_type === 'SHOTS_UPDATE') {
          summary = `Pieces updated: ${fmtNumber(a.actual_shots_previous) || '?'} → ${fmtNumber(a.actual_shots_new) || '?'}`;
        } else if (a.assessment_type === 'CONDITION_REVIEW') {
          summary = `Condition: ${a.condition_score_previous ?? '?'} → ${a.condition_score_new ?? '?'}`;
        }
        return (
          <div key={a.id || i} className="ldp-record-row">
            <span className="ldp-record-value">
              <strong>{a.assessment_type}</strong>
              <span className="ldp-record-desc">{fmtDate(a.assessment_date)}</span>
              <span className="ldp-record-desc">{a.assessed_by}</span>
            </span>
            <span className="ldp-record-desc">{summary}</span>
          </div>
        );
      })}
    </div>
  );
}
